import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useSpaces } from '../contexts/SpacesContext';
import { createPaymentIntent } from '../services/stripeService';
import { markTermsAccepted, updateBookingPaymentMethod, markPaymentComplete } from '../services/bookingService';
import { sendBookingConfirmation } from '../services/emailService';
import { sendBookingSms } from '../services/smsService';

const stripePromise = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY)
  : null;

const toD = (v) => v?.toDate ? v.toDate() : (v ? new Date(v) : null);

function StripeForm({ bookingId, booking, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setProcessing(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: `${window.location.origin}/pay?bookingId=${bookingId}&paid=1` },
      redirect: 'if_required',
    });
    if (error) {
      toast.error(error.message || 'Payment failed.');
      setProcessing(false);
    } else {
      onSuccess(paymentIntent?.id);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />
      <button type="submit" disabled={!stripe || processing} className="btn-primary w-full mt-2 disabled:opacity-50">
        {processing ? 'Processing…' : `Pay $${booking.price}`}
      </button>
    </form>
  );
}

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('bookingId');
  const paidParam = searchParams.get('paid');
  const spaces = useSpaces();

  const [booking, setBooking] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paid, setPaid] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!bookingId) { setError('No booking ID provided.'); setLoading(false); return; }

    getDoc(doc(db, 'bookings', bookingId))
      .then(async (snap) => {
        if (!snap.exists()) { setError('Booking not found.'); setLoading(false); return; }
        const b = { id: snap.id, ...snap.data() };
        setBooking(b);

        if (paidParam === '1' || b.paymentStatus === 'paid') {
          setPaid(true);
          setLoading(false);
          return;
        }

        if (stripePromise && b.price > 0) {
          try {
            const { clientSecret: cs } = await createPaymentIntent({ amount: b.price * 100, bookingId, currency: 'aud' });
            setClientSecret(cs);
          } catch {}
        }
        setLoading(false);
      })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, [bookingId, paidParam]);

  const handlePaymentSuccess = (paymentIntentId) => {
    setPaid(true);
    if (bookingId) {
      markTermsAccepted(bookingId).catch(() => {});
      updateBookingPaymentMethod(bookingId, 'card').catch(() => {});
      markPaymentComplete(bookingId, paymentIntentId).catch(() => {});
    }
    const space = booking ? spaces.find((s) => s.id === booking.spaceId) : null;
    if (booking?.email) {
      sendBookingConfirmation({
        to: booking.email,
        name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email,
        bookingId,
        spaceName: space?.name || '',
        seatName: booking.seatName || '',
        startDate: toD(booking.startDate) ? format(toD(booking.startDate), 'd MMM yyyy') : '',
        endDate: toD(booking.endDate) ? format(toD(booking.endDate), 'd MMM yyyy') : '',
        price: booking.price,
        pricingType: booking.pricingType,
        paymentMethod: 'card',
      }).catch(() => {});
    }
    if (booking?.phone) {
      sendBookingSms({
        to: booking.phone,
        name: `${booking.firstName || ''} ${booking.lastName || ''}`.trim() || booking.email,
        spaceName: space?.name || '',
        startDate: toD(booking.startDate) ? format(toD(booking.startDate), 'd MMM yyyy') : '',
        endDate: toD(booking.endDate) ? format(toD(booking.endDate), 'd MMM yyyy') : '',
        bookingId,
      }).catch(() => {});
    }
    toast.success('Payment successful!');
  };

  const space = booking ? spaces.find((s) => s.id === booking.spaceId) : null;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Link to="/" className="btn-primary">Go Home</Link>
        </div>
      </div>
    );
  }

  if (paid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="card p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">Payment Confirmed!</h2>
          <p className="text-slate-500 mb-6">Your booking at The Hub Crookwell is confirmed. Check your email for details.</p>
          <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <Link to="/" className="inline-block mb-4">
            <img src="/images/mainlogo.jpg" alt="The Hub Crookwell" className="h-16 mx-auto" />
          </Link>
          <h1 className="text-2xl font-extrabold text-slate-800">Complete Your Booking</h1>
          <p className="text-slate-500 mt-1">Secure your space at The Hub Crookwell.</p>
        </div>

        {/* Booking summary */}
        <div className="card p-5 mb-6">
          <h3 className="font-semibold text-slate-700 mb-3">Booking Summary</h3>
          <div className="space-y-1.5 text-sm text-slate-600">
            <div className="flex justify-between">
              <span>Space</span>
              <span className="font-medium">{space?.name || booking?.spaceId}</span>
            </div>
            {booking?.seatName && (
              <div className="flex justify-between">
                <span>Seat</span>
                <span className="font-medium">{booking.seatName}</span>
              </div>
            )}
            {toD(booking?.startDate) && (
              <div className="flex justify-between">
                <span>Start</span>
                <span className="font-medium">{format(toD(booking.startDate), 'd MMM yyyy')}</span>
              </div>
            )}
            {toD(booking?.endDate) && (
              <div className="flex justify-between">
                <span>End</span>
                <span className="font-medium">{format(toD(booking.endDate), 'd MMM yyyy')}</span>
              </div>
            )}
            {booking?.originalPrice > booking?.price && (
              <div className="flex justify-between text-slate-400">
                <span>Original price</span>
                <span className="line-through">${booking.originalPrice}</span>
              </div>
            )}
            <div className="flex justify-between font-semibold text-slate-800 pt-2 border-t border-slate-100 mt-2">
              <span>Total due</span>
              <span>${booking?.price ?? 0}</span>
            </div>
          </div>
        </div>

        {/* Payment form */}
        <div className="card p-6">
          {!stripePromise ? (
            <p className="text-sm text-slate-500 text-center">Online payment is not configured. Please contact us to complete your booking.</p>
          ) : !clientSecret ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-4 border-brand-500 border-t-transparent rounded-full" />
            </div>
          ) : (
            <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: 'stripe' } }}>
              <StripeForm bookingId={bookingId} booking={booking} onSuccess={(intentId) => handlePaymentSuccess(intentId)} />
            </Elements>
          )}
        </div>
      </div>
    </div>
  );
}
